import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { LinkSimple, LinkBreak, ArrowCounterClockwise } from "@phosphor-icons/react";

const SCOPE_LABELS = { billing: "Billing", health: "Health", photos: "Photos", messages: "Messages", reports: "Daily report" };

const emptyForm = {
  email: "", name: "", phone: "", relationship: "Parent", permission_level: "full",
  legal_guardian: false, pickup_authorized: true, billing_responsible: false, reason: "",
};

const ConnectDialog = ({ student, levels, reload }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/portal/connections", { ...form, student_id: student.student_id });
      toast.success(`${form.email} connected to ${student.name}`);
      setOpen(false);
      setForm(emptyForm);
      await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full font-bold"
                data-testid={`connect-btn-${student.student_id}`}>
          <LinkSimple size={15} className="mr-2" /> Connect a parent
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Connect a parent to {student.name}</DialogTitle>
          <DialogDescription>
            The person must already have an account, or they can create one and sign in once connected.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-bold">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => set("email")(e.target.value)}
                   className="mt-2 h-11 rounded-xl" data-testid="connect-email" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-bold">Name</Label>
              <Input value={form.name} onChange={(e) => set("name")(e.target.value)}
                     className="mt-2 h-11 rounded-xl" data-testid="connect-name" />
            </div>
            <div>
              <Label className="text-sm font-bold">Relationship</Label>
              <Input value={form.relationship} onChange={(e) => set("relationship")(e.target.value)}
                     className="mt-2 h-11 rounded-xl" data-testid="connect-relationship" />
            </div>
          </div>
          <div>
            <Label className="text-sm font-bold">Access level</Label>
            <Select value={form.permission_level} onValueChange={set("permission_level")}>
              <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="connect-level"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(levels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-3">
            {[["legal_guardian", "Legal guardian"], ["pickup_authorized", "May collect the child"],
              ["billing_responsible", "Billing contact"]].map(([k, label]) => (
              <div key={k} className="flex items-center justify-between gap-4">
                <span className="text-sm font-bold">{label}</span>
                <Switch checked={form[k]} onCheckedChange={set(k)} data-testid={`connect-${k}`} />
              </div>
            ))}
          </div>
          <div>
            <Label className="text-sm font-bold">Why (recorded in the audit trail)</Label>
            <Input value={form.reason} onChange={(e) => set("reason")(e.target.value)}
                   className="mt-2 h-11 rounded-xl" data-testid="connect-reason" />
          </div>
          <Button onClick={save} disabled={busy || !form.email}
                  className="w-full h-11 rounded-full font-bold bg-[#3B82F6] hover:bg-[#2563EB]"
                  data-testid="connect-save">
            {busy ? "Connecting…" : "Connect"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const GuardianRow = ({ g, levels, scopes, reload }) => {
  const [busy, setBusy] = useState(false);

  const changeLevel = async (level) => {
    setBusy(true);
    try {
      await api.patch(`/portal/connections/${g.id}`, { permission_level: level });
      toast.success("Access level updated");
      await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const toggleScope = async (scope, value) => {
    setBusy(true);
    try {
      await api.patch(`/portal/connections/${g.id}`, {
        permission_level: "custom", access: { ...g.access, [scope]: value } });
      toast.success(`${SCOPE_LABELS[scope]} access ${value ? "granted" : "removed"}`);
      await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const detach = async () => {
    setBusy(true);
    try {
      await api.post(`/portal/connections/${g.id}/detach`, { reason: "Detached from portal controls" });
      toast.success("Detached — this can be undone");
      await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const restore = async () => {
    setBusy(true);
    try {
      await api.post(`/portal/connections/${g.id}/restore`);
      toast.success("Reconnected");
      await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className={`rounded-2xl border px-4 py-3 ${g.active ? "border-border" : "border-dashed border-slate-300 bg-slate-50"}`}
         data-testid={`guardian-${g.id || g.email}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold truncate">
            {g.name || g.email}
            {g.legal_guardian && <span className="ml-2 text-xs font-bold text-emerald-700">Legal guardian</span>}
            {g.billing_responsible && <span className="ml-2 text-xs font-bold text-[#3B82F6]">Billing</span>}
            {!g.pickup_authorized && <span className="ml-2 text-xs font-bold text-[#EF4444]">No pickup</span>}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {g.email} · {g.relationship || "Guardian"} · {g.has_account ? "has an account" : "no account yet"}
            {!g.active ? " · detached" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {g.implicit ? (
            <span className="text-xs font-bold text-muted-foreground">From the student record</span>
          ) : (
            <>
              <Select value={g.permission_level} onValueChange={changeLevel} disabled={busy}>
                <SelectTrigger className="h-9 w-40 rounded-xl" data-testid={`level-${g.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(levels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              {g.active ? (
                <Button size="sm" variant="outline" disabled={busy} onClick={detach}
                        className="rounded-full font-bold" data-testid={`detach-${g.id}`}>
                  <LinkBreak size={15} />
                </Button>
              ) : (
                <Button size="sm" variant="outline" disabled={busy} onClick={restore}
                        className="rounded-full font-bold" data-testid={`restore-${g.id}`}>
                  <ArrowCounterClockwise size={15} />
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      {!g.implicit && g.active && (
        <div className="mt-3 flex flex-wrap gap-4">
          {scopes.map((s) => (
            <label key={s} className="flex items-center gap-2 text-xs font-bold">
              <Switch checked={g.access[s] !== false} disabled={busy}
                      onCheckedChange={(v) => toggleScope(s, v)}
                      data-testid={`scope-${g.id}-${s}`} />
              {SCOPE_LABELS[s]}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default function ConnectionsTab() {
  const [data, setData] = useState(null);
  const [showDetached, setShowDetached] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/portal/connections", { params: { include_detached: showDetached } });
      setData(d);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [showDetached]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-muted-foreground" data-testid="connections-loading">Loading connections…</p>;

  return (
    <div data-testid="connections-tab">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Every parent and guardian attached to each child, and exactly what each of them can see.
        </p>
        <label className="flex items-center gap-2 text-sm font-bold shrink-0">
          <Switch checked={showDetached} onCheckedChange={setShowDetached} data-testid="show-detached" />
          Show detached
        </label>
      </div>
      <div className="mt-6 space-y-4">
        {data.students.map((s) => (
          <div key={s.student_id} className="bg-white rounded-3xl border border-border p-5"
               data-testid={`connection-student-${s.student_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-display text-lg font-semibold">{s.name}</p>
                <p className="text-xs text-muted-foreground font-semibold">
                  {s.room || "No classroom"} · {s.status}
                  {s.media_consent === true ? " · photos allowed"
                    : s.media_consent === false ? " · photos not allowed" : " · photo consent not set"}
                </p>
              </div>
              <ConnectDialog student={s} levels={data.permission_levels} reload={load} />
            </div>
            <div className="mt-4 space-y-2">
              {s.guardians.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid={`no-guardians-${s.student_id}`}>
                  Nobody is connected to this child yet.
                </p>
              )}
              {s.guardians.map((g) => (
                <GuardianRow key={g.id || g.email} g={g} levels={data.permission_levels}
                             scopes={data.access_scopes} reload={load} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
